import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Check } from "lucide-react";
import { MENU } from "@/data/content";

const card = {
  hidden: { opacity: 0, y: 50 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Category({ cat, i, open, onToggle }) {
  const wide = i < 2; // prime due categorie piu' larghe
  return (
    <motion.div
      variants={card}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      className={`group relative rounded-xl overflow-hidden border border-[#E8E3D9] bg-[#F5F2EB] ${
        wide ? "sm:col-span-3" : "sm:col-span-2"
      }`}
      data-testid={`menu-cat-${cat.key}`}
    >
      <div className="relative h-64 sm:h-72 overflow-hidden">
        <img
          src={cat.image}
          alt={cat.title}
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A1B14]/70 via-transparent to-transparent" />
        <span className="absolute top-4 left-4 font-body text-[10px] tracking-[0.2em] uppercase bg-[#FDFBF7]/90 text-[#2A1B14] px-3 py-1 rounded-full">
          {cat.tag}
        </span>
        <div className="absolute bottom-4 left-5 right-5">
          <h3 className="font-display text-4xl sm:text-5xl text-[#FDFBF7] leading-none">
            {cat.title}
          </h3>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <p className="font-body text-[#5C4F4A] text-sm sm:text-base leading-relaxed">
          {cat.desc}
        </p>
        <button
          onClick={onToggle}
          className="mt-4 inline-flex items-center gap-2 font-body text-sm font-semibold text-[#2A1B14] link-underline"
          data-testid={`menu-toggle-${cat.key}`}
        >
          {open ? <Minus size={16} /> : <Plus size={16} />}
          {open ? "Chiudi" : "Vedi cosa trovi"}
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.ul
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden mt-2"
            >
              {cat.items.map((it) => (
                <li
                  key={it}
                  className="flex items-start gap-3 py-2 border-t border-[#E8E3D9] font-body text-sm text-[#2A1B14]/90"
                >
                  <Check size={16} className="text-[#8C9B86] mt-0.5 shrink-0" />
                  {it}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default function Menu() {
  const [open, setOpen] = useState(null);
  return (
    <section id="menu" className="bg-[#FDFBF7] py-16 sm:py-20" data-testid="menu-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div>
            <p className="font-body text-xs tracking-[0.28em] uppercase text-[#8C9B86] mb-4">
              Il nostro banco
            </p>
            <h2 className="font-display font-light tracking-tight leading-[0.95] text-[clamp(2.5rem,6vw,5rem)] text-[#2A1B14]">
              Menu in <span className="italic">evidenza</span>
            </h2>
          </div>
          <p className="font-body text-[#5C4F4A] max-w-xs text-sm sm:text-base leading-relaxed">
            Colazione, pasticceria, salateria, pane e caffetteria. Espandi ogni
            categoria per scoprire cosa sforniamo.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-6 gap-6 lg:gap-8">
          {MENU.map((cat, i) => (
            <Category
              key={cat.key}
              cat={cat}
              i={i}
              open={open === cat.key}
              onToggle={() => setOpen(open === cat.key ? null : cat.key)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
